<template>
  <div class="chart-container">
    <component
      ref="chartRef"
      :is="chartComponent"
      :data="chartData"
      :width="width"
      :height="height"
      :options="chartOptions"
      v-if="chartData && chartOptions"
    />
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import { Bar, Line, Pie, Doughnut, Bubble, PolarArea, Radar, Scatter } from 'vue-chartjs'
import { Chart as ChartJS } from 'chart.js'

const whiteBackgroundPlugin = {
  id: 'whiteBackground',
  beforeDraw(chart) {
    const ctx = chart.ctx
    ctx.save()
    ctx.globalCompositeOperation = 'destination-over'
    ctx.fillStyle = '#fff'
    ctx.fillRect(0, 0, chart.width, chart.height)
    ctx.restore()
  }
}

export default defineComponent({
  name: 'GenericChart',
  props: {
    chartConfig: {
      type: Object,
      required: true
    },
    autoColors: {
      type: Boolean,
      default: false
    },
    width: {
      type: Number,
      default: 400
    },
    height: {
      type: Number,
      default: 400
    }
  },
  computed: {
    chartComponent() {
      const type = this.chartConfig?.type
      const chartComponents = {
        bar: Bar,
        line: Line,
        pie: Pie,
        doughnut: Doughnut,
        bubble: Bubble,
        polarArea: PolarArea,
        radar: Radar,
        scatter: Scatter,
      };
      return chartComponents[type] || Bar
    },
    chartData() {
      return this.chartConfig?.data || null
    },
    chartOptions() {
      const baseOptions = this.chartConfig?.options || {}
      return {
        ...baseOptions,
        plugins: {
          ...(baseOptions.plugins || {}),
          autocolors: {
            mode: 'data',
            enabled: this.autoColors,
          },
          whiteBackground: true
        }
      }
    }
  },
  mounted() {
    if (!ChartJS.registry.plugins.get('whiteBackground')) {
      ChartJS.register(whiteBackgroundPlugin)
    }

    this.defineChartWidthAndHeight(this.$refs.chartRef)
  },
  methods: {
    defineChartWidthAndHeight(chartComponent) {
      this.$nextTick(() => {
        let canvas = chartComponent?.$el
        if (canvas && canvas.tagName !== 'CANVAS') {
          canvas = canvas.querySelector('canvas')
        }

        if (canvas) {
          canvas.style.setProperty('max-height', '500px', 'important')

          const totalLabels = this.chartConfig.data.labels.length

          if (totalLabels > 50) {
            const extraWidth = totalLabels * 10
            const container = chartComponent.$el
            if (container) {
              container.style.width = `${container.offsetWidth + extraWidth}px`
            }
          }
        }
      })
    },
    downloadChart() {
      const chartComponent = this.$refs.chartRef
      const canvas = chartComponent?.$el?.querySelector('canvas') || chartComponent?.$el
      const chart = ChartJS.getChart(canvas)

      if (chart) {
        const url = chart.toBase64Image()
        const link = document.createElement('a')
        link.href = url
        link.download = this.$t('chart') + '.png'
        link.click()
      } else {
        alert(this.$t('chartDownloadError'))
      }
    }
  }
})
</script>

<style scoped>
.chart-container {
  position: relative;
  height: 100%;
  width: 100%;
  overflow: auto;
}
</style>
